import React, { useState, useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  Box,
  Paper,
  Button,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Snackbar,
} from '@mui/material';
import lamejs from 'lamejs';
import { useDropzone } from 'react-dropzone';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { sendMailViaGraph, buildSRTEmailHTML } from '../../utils/graphMailService';
import { BACKEND_URL, AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { voerUitgesteldUit } from '../../utils/uitgesteld';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import PageHeader from '../../ui-components/PageHeader';
import PresetManager from './PresetManager';
import KaraokePresetManager from './KaraokePresetManager';
import StepVideoInput from './StepVideoInput';
import StepSettings from './StepSettings';
import StepGenerate from './StepGenerate';
import StepReview from './StepReview';
import StepResult from './StepResult';

// Backend URL - use centralized config
const API_URL = BACKEND_URL;

const steps = ['Video invoer', 'Instellingen & tekst', 'SRT Genereren', 'Review & Render', 'Resultaat'];

function SRTGenerator() {
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  // Step 1: Video input
  const [videoFile, setVideoFile] = useState(null);
  const [frameIoLink, setFrameIoLink] = useState('');
  const [inputMethod, setInputMethod] = useState('upload'); // 'upload' or 'link'

  // Step 2: Settings
  const [language, setLanguage] = useState('auto'); // Let Whisper AI detect language
  const [aspectRatio, setAspectRatio] = useState('16:9'); // '16:9', '9:16', '1:1'
  const [subtitleStyle, setSubtitleStyle] = useState('outline'); // 'outline' (wit + zwart outline) or 'box' (zwarte achtergrond)
  const [clientPresets, setClientPresets] = useState([]); // All presets from AI Engine (excl. __default__)
  const [selectedClientPreset, setSelectedClientPreset] = useState(''); // Selected client name
  const [activePreset, setActivePreset] = useState(null); // Active preset for current aspect ratio
  const [defaultPreset, setDefaultPreset] = useState(null); // __default__ preset for current aspect ratio
  const [presetManagerOpen, setPresetManagerOpen] = useState(false);
  const [karaokePresetManagerOpen, setKaraokePresetManagerOpen] = useState(false);
  const [overridePreset, setOverridePreset] = useState(false);
  const [scriptFile, setScriptFile] = useState(null);
  const [scriptText, setScriptText] = useState('');
  const [skipCorrection, setSkipCorrection] = useState(true); // Default: skip AI correction when script provided
  const [email, setEmail] = useState(activeAccount?.username || '');
  const [sendEmail, setSendEmail] = useState(true); // Default AAN
  const [emailSent, setEmailSent] = useState(false);
  const [uploadToFrameIo, setUploadToFrameIo] = useState(true); // Default AAN
  const [selectedFolderId, setSelectedFolderId] = useState(null); // Selected target folder from cascading picker
  const [selectedFolderPath, setSelectedFolderPath] = useState(''); // Selected folder path for display
  const [subtitleSize, setSubtitleSize] = useState('medium'); // small, medium, large, xlarge
  const [subtitleFont, setSubtitleFont] = useState(''); // Empty = default/auto
  const [availableFonts, setAvailableFonts] = useState([]);
  const [customFonts, setCustomFonts] = useState([]);
  const fontInputRef = useRef(null);
  const [ellipsisStyle, setEllipsisStyle] = useState('end_only'); // 'none', 'end_only', 'both'
  const [timingOffset, setTimingOffset] = useState(0); // Timing offset in ms - default 0ms (no delay)
  const [showAdvanced, setShowAdvanced] = useState(false); // Show advanced options
  const [frameioLink, setFrameioLink] = useState(null);
  const [hasBurnedVideo, setHasBurnedVideo] = useState(false);

  // Karaoke mode
  const [isKaraoke, setIsKaraoke] = useState(false);
  const [karaokeASS, setKaraokeASS] = useState(null);
  const [karaokeStyle, setKaraokeStyle] = useState({
    fontName: 'Arial',
    fontSize: 48,
    bold: true,
    uppercase: false,
    textColor: '#FFFFFF',
    textOpacity: 100,
    highlightColor: '#00BBAA',
    highlightOpacity: 100,
    bgColor: '#000000',
    bgOpacity: 0,
    outlineEnabled: true,
    outlineColor: '#000000',
    outlineOpacity: 100,
    outlineWidth: 3,
    shadowEnabled: true,
    shadowColor: '#000000',
    shadowOpacity: 60,
    shadowDepth: 2,
    animationType: 'fade',
    animDurationMs: 150,
    alignment: 2,
    marginV: 60,
    marginL: 40,
    marginR: 40,
  });

  // Step 3: Review & Edit
  const [editableSRT, setEditableSRT] = useState('');
  const [jobId, setJobId] = useState(null);
  const [isRendering, setIsRendering] = useState(false);
  const [corrections, setCorrections] = useState([]); // AI corrections for review
  const [correctionStatus, setCorrectionStatus] = useState({}); // { index: 'accepted' | 'rejected' | 'editing' }
  const [correctionEdits, setCorrectionEdits] = useState({}); // { index: editedText }
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null); // For audio playback in review
  const [wordTimestamps, setWordTimestamps] = useState(null); // Word-level timestamps for WordTimingEditor
  const [playbackTime, setPlaybackTime] = useState(null); // Current playback time for word highlighting
  const [showWordEditor, setShowWordEditor] = useState(false); // Toggle for WordTimingEditor (default: textarea)
  const videoRef = useRef(null);

  // Step 5: Result
  const [generatedSRT, setGeneratedSRT] = useState(null);
  const [correctedSRT, setCorrectedSRT] = useState(null);
  const [fcpxml, setFcpxml] = useState(null);
  const [detectedLanguage, setDetectedLanguage] = useState(null); // Track actual detected language
  const [feedback, setFeedback] = useState('');
  const [ukEnglishSRT, setUkEnglishSRT] = useState(null);
  const [convertingToUK, setConvertingToUK] = useState(false);
  const [translatedSRT, setTranslatedSRT] = useState(null);
  const [translating, setTranslating] = useState(false);

  // Custom vocabulary
  const [vocabularyTerms, setVocabularyTerms] = useState([]);
  const [vocabLoaded, setVocabLoaded] = useState(false);
  const [newVocabTerm, setNewVocabTerm] = useState('');
  const [newVocabReplacement, setNewVocabReplacement] = useState('');
  const [newVocabCategory, setNewVocabCategory] = useState('');
  const [showVocabulary, setShowVocabulary] = useState(false);

  // Auto-detection state
  const [analyzing, setAnalyzing] = useState(false);
  const [videoInfo, setVideoInfo] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Analyze video for auto-detection
  const analyzeVideo = useCallback(async (file, frameioUrl = null) => {
    setAnalyzing(true);
    setVideoInfo(null);

    try {
      const formData = new FormData();
      if (file) {
        formData.append('video', file);
      } else if (frameioUrl) {
        formData.append('frameIoLink', frameioUrl);
      }

      const response = await authFetch(`${API_URL}/Api/srt-generator/analyze`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setVideoInfo(data);

        // Auto-fill detected values (only aspect ratio - language detection is unreliable)
        if (data.detectedAspectRatio) {
          setAspectRatio(data.detectedAspectRatio);
        }
        // NOTE: We don't auto-fill language because detection is unreliable
        // Default is Dutch (nl) which is correct for most Full Frame videos

        // Show success message based on detection method
        let detectionMsg = '';

        if (data.detectionMethod === 'resolution' && data.resolution) {
          detectionMsg = `✨ Gedetecteerd: ${data.resolution.width}×${data.resolution.height} (${data.detectedAspectRatio})`;
        } else if (data.detectionMethod === 'filename') {
          detectionMsg = `📝 Aspectratio afgeleid uit bestandsnaam: ${data.detectedAspectRatio}`;
        } else if (data.detectionMethod === 'audio' || data.isAudioOnly) {
          detectionMsg = `🎵 Audiobestand gedetecteerd`;
        } else {
          detectionMsg = `⚠️ Kon bestand niet volledig analyseren - controleer instellingen`;
        }

        setSnackbar({
          open: true,
          message: detectionMsg,
          severity: data.detectionMethod === 'default' ? 'warning' : 'success'
        });
      } else {
        console.warn('Video analysis failed, using defaults');
      }
    } catch (err) {
      console.warn('Could not analyze video:', err);
    } finally {
      setAnalyzing(false);
    }
  }, []);

  // Convert WAV to MP3 in browser
  const convertWavToMp3 = useCallback(async (wavFile, onProgress) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target.result;
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();

          onProgress?.(10, 'Audio decoderen...');
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

          // Get audio data
          const numberOfChannels = audioBuffer.numberOfChannels;
          const sampleRate = audioBuffer.sampleRate;
          const samples = audioBuffer.length;

          onProgress?.(30, 'MP3 encoder initialiseren...');

          // Create MP3 encoder (128kbps, good quality for speech)
          const mp3encoder = new lamejs.Mp3Encoder(numberOfChannels, sampleRate, 128);
          const mp3Data = [];

          // Get channel data
          const leftChannel = audioBuffer.getChannelData(0);
          const rightChannel = numberOfChannels > 1 ? audioBuffer.getChannelData(1) : leftChannel;

          // Convert Float32 to Int16
          const leftSamples = new Int16Array(samples);
          const rightSamples = new Int16Array(samples);

          for (let i = 0; i < samples; i++) {
            leftSamples[i] = Math.max(-32768, Math.min(32767, leftChannel[i] * 32768));
            rightSamples[i] = Math.max(-32768, Math.min(32767, rightChannel[i] * 32768));
          }

          onProgress?.(50, 'Converteren naar MP3...');

          // Encode in chunks
          const sampleBlockSize = 1152;
          for (let i = 0; i < samples; i += sampleBlockSize) {
            const leftChunk = leftSamples.subarray(i, i + sampleBlockSize);
            const rightChunk = rightSamples.subarray(i, i + sampleBlockSize);

            const mp3buf = numberOfChannels > 1
              ? mp3encoder.encodeBuffer(leftChunk, rightChunk)
              : mp3encoder.encodeBuffer(leftChunk);

            if (mp3buf.length > 0) {
              mp3Data.push(mp3buf);
            }

            // Progress update every 10%
            const prog = 50 + Math.round((i / samples) * 40);
            if (i % (sampleBlockSize * 100) === 0) {
              onProgress?.(prog, 'Converteren naar MP3...');
            }
          }

          // Flush remaining data
          const mp3buf = mp3encoder.flush();
          if (mp3buf.length > 0) {
            mp3Data.push(mp3buf);
          }

          onProgress?.(95, 'MP3 bestand maken...');

          // Create MP3 blob
          const blob = new Blob(mp3Data, { type: 'audio/mp3' });
          const mp3File = new File(
            [blob],
            wavFile.name.replace(/\.wav$/i, '.mp3'),
            { type: 'audio/mp3' }
          );

          onProgress?.(100, 'Conversie voltooid!');

          // Close audio context
          audioContext.close();

          resolve(mp3File);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Kon WAV bestand niet lezen'));
      reader.readAsArrayBuffer(wavFile);
    });
  }, []);

  // State for WAV conversion progress
  const [convertingWav, setConvertingWav] = useState(false);
  const [wavConversionProgress, setWavConversionProgress] = useState({ percent: 0, message: '' });

  // Video dropzone
  const onDropVideo = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];

      // Check for large WAV files - auto-convert to MP3
      const isWav = file.name.toLowerCase().endsWith('.wav');
      const fileSizeMB = file.size / (1024 * 1024);

      if (isWav && fileSizeMB > 25) {
        // Auto-convert WAV to MP3
        setConvertingWav(true);
        setWavConversionProgress({ percent: 0, message: 'WAV naar MP3 conversie starten...' });
        setError(null);

        try {
          const mp3File = await convertWavToMp3(file, (percent, message) => {
            setWavConversionProgress({ percent, message });
          });

          const mp3SizeMB = (mp3File.serverSize || mp3File.size) / (1024 * 1024);
          setSnackbar({
            open: true,
            message: `WAV geconverteerd naar MP3: ${fileSizeMB.toFixed(1)}MB → ${mp3SizeMB.toFixed(1)}MB`,
            severity: 'success'
          });

          setVideoFile(mp3File);
          setInputMethod('upload');
          setFrameIoLink('');
          analyzeVideo(mp3File);
        } catch (err) {
          console.error('Browser WAV conversion failed, trying server-side:', err);
          setWavConversionProgress({ percent: 50, message: 'Browser conversie mislukt, server-side proberen...' });

          // Try server-side conversion as fallback
          try {
            const formData = new FormData();
            formData.append('audio', file);

            const response = await authFetch(`${API_URL}/Api/srt-generator/convert-audio`, {
              method: 'POST',
              body: formData
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error || 'Server conversie mislukt');
            }

            const result = await response.json();
            console.log('Server-side conversion result:', result);

            setSnackbar({
              open: true,
              message: result.message,
              severity: 'success'
            });

            // Create a placeholder file object with server path
            const serverFile = new File([new Blob()], file.name.replace('.wav', '.mp3'), { type: 'audio/mp3' });
            serverFile.serverPath = result.outputPath;
            serverFile.serverSize = result.compressedSize * 1024 * 1024;

            setVideoFile(serverFile);
            setInputMethod('upload');
            setFrameIoLink('');
            analyzeVideo(serverFile);
          } catch (serverErr) {
            console.error('Server-side conversion also failed:', serverErr);
            setError(`WAV conversie mislukt: ${serverErr.message}. Probeer een kleiner bestand of gebruik Frame.io link.`);
          }
        } finally {
          setConvertingWav(false);
        }
        return;
      }

      // Check for large MP3 files (>25MB) - need server-side compression
      const isMp3 = file.name.toLowerCase().endsWith('.mp3');
      if (isMp3 && fileSizeMB > 25) {
        console.log(`Large MP3 detected: ${fileSizeMB.toFixed(1)} MB, needs compression`);
        setConvertingWav(true);
        setWavConversionProgress({ percent: 0, message: 'Groot MP3 bestand comprimeren...' });
        setError(null);

        try {
          const formData = new FormData();
          formData.append('audio', file);

          const response = await authFetch(`${API_URL}/Api/srt-generator/convert-audio`, {
            method: 'POST',
            body: formData
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Compressie mislukt');
          }

          const result = await response.json();
          console.log('Server-side compression result:', result);

          setSnackbar({
            open: true,
            message: result.message,
            severity: 'success'
          });

          // Create a placeholder file object with server path
          const serverFile = new File([new Blob()], file.name, { type: 'audio/mp3' });
          serverFile.serverPath = result.outputPath;
          serverFile.serverSize = result.compressedSize * 1024 * 1024;

          setVideoFile(serverFile);
          setInputMethod('upload');
          setFrameIoLink('');
          analyzeVideo(serverFile);
        } catch (compressErr) {
          console.error('MP3 compression failed:', compressErr);
          setError(`MP3 compressie mislukt: ${compressErr.message}. Bestand is te groot voor transcriptie (max 25MB).`);
        } finally {
          setConvertingWav(false);
        }
        return;
      }

      setVideoFile(file);
      setInputMethod('upload');
      setFrameIoLink('');

      // Auto-analyze the video
      analyzeVideo(file);
    }
  }, [analyzeVideo, convertWavToMp3]);

  const { getRootProps: getVideoRootProps, getInputProps: getVideoInputProps, isDragActive: isVideoDragActive } = useDropzone({
    onDrop: onDropVideo,
    accept: {
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov'],
      'audio/mpeg': ['.mp3'],
      'audio/wav': ['.wav']
    },
    maxFiles: 1,
    maxSize: 500 * 1024 * 1024 // 500MB
  });

  // Script dropzone
  const onDropScript = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setScriptFile(file);
      if (file.name.endsWith('.txt')) {
        const reader = new FileReader();
        reader.onload = (e) => setScriptText(e.target.result);
        reader.readAsText(file);
      } else {
        // .docx/.doc → backend parsing met mammoth
        try {
          const formData = new FormData();
          formData.append('file', file);
          const res = await authFetch(`${API_URL}/Api/docx/parse-docx`, { method: 'POST', body: formData });
          if (!res.ok) throw new Error('Kon document niet parsen');
          const data = await res.json();
          setScriptText(data.content || '');
        } catch (err) {
          console.error('Error parsing docx:', err);
          setError('Kon Word-document niet uitlezen. Probeer een .txt bestand of plak de tekst handmatig.');
          setScriptFile(null);
        }
      }
    }
  }, []);

  const { getRootProps: getScriptRootProps, getInputProps: getScriptInputProps } = useDropzone({
    onDrop: onDropScript,
    accept: {
      'text/plain': ['.txt'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxFiles: 1
  });

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate video input
      if (!videoFile && !frameIoLink) {
        setError('Upload een video of voer een Frame.io link in');
        return;
      }
    }
    if (activeStep === 1) {
      // Validate email
      if (!email || !email.includes('@')) {
        setError('Voer een geldig emailadres in');
        return;
      }
      // Auto-start generation when going from settings to generation step
      setError(null);
      setActiveStep(2);
      // Small delay to ensure state updates before starting
      setTimeout(() => handleGenerate(), 100);
      return;
    }
    setError(null);
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError(null);
  };

  const handleGenerate = async () => {
    setLoading(true);
    setProgress(0);
    setProgressMessage('Voorbereiden...');
    setError(null);

    try {
      const formData = new FormData();

      if (videoFile?.serverPath) {
        // WAV was converted server-side — reference the converted MP3 by path instead of uploading empty placeholder
        formData.append('serverAudioPath', videoFile.serverPath);
        formData.append('originalFilename', videoFile.name);
        console.log('[SRT] Using server-converted audio:', videoFile.serverPath);
      } else if (videoFile) {
        formData.append('video', videoFile);
        formData.append('originalFilename', videoFile.name);
      } else if (frameIoLink) {
        formData.append('frameIoLink', frameIoLink);
      }

      formData.append('language', language);
      formData.append('aspectRatio', aspectRatio);
      formData.append('subtitleStyle', subtitleStyle);
      formData.append('subtitleSize', subtitleSize);
      if (subtitleFont) formData.append('subtitleFont', subtitleFont);
      formData.append('ellipsisStyle', ellipsisStyle);
      formData.append('timingOffset', String(timingOffset));
      formData.append('email', email);
      formData.append('isKaraoke', String(isKaraoke));
      if (isKaraoke) {
        formData.append('karaokeStyle', JSON.stringify(karaokeStyle));
      }
      // Stijl prioriteit: klantpreset > standaard preset
      const effectivePreset = (activePreset && !overridePreset) ? activePreset : defaultPreset;
      if (effectivePreset) {
        formData.append('presetStyle', JSON.stringify({
          font_name: effectivePreset.font_name,
          font_style: effectivePreset.font_style,
          font_size: effectivePreset.font_size,
          baseline: effectivePreset.baseline
        }));
      }
      // Don't upload to Frame.io yet - wait for review step
      formData.append('uploadToFrameIo', 'false');

      if (scriptText) {
        formData.append('script', scriptText);
        formData.append('skipCorrection', skipCorrection ? 'true' : 'false');
      }

      setProgress(10);
      setProgressMessage('Video uploaden naar server...');

      console.log('Uploading to:', `${API_URL}/Api/srt-generator/generate`);

      const response = await authFetch(`${API_URL}/Api/srt-generator/generate`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || `Server error: ${response.status}`;
        } catch {
          errorMessage = `Server error: ${response.status} - ${errorText.substring(0, 100)}`;
        }
        throw new Error(errorMessage);
      }

      // Poll for progress
      const result = await response.json();
      console.log('Job created:', result);

      if (!result.jobId) {
        throw new Error('Geen job ID ontvangen van server');
      }

      await pollProgress(result.jobId);

    } catch (err) {
      console.error('Generate error:', err);
      toast.error('Fout bij genereren ondertiteling');
      setError(err.message || 'Onbekende fout bij uploaden');
      setLoading(false);
    }
  };

  const pollProgress = async (jobId) => {
    console.log('Starting to poll job:', jobId);
    let errorCount = 0;
    const maxErrors = 5; // Allow up to 5 consecutive errors before giving up

    const pollInterval = setInterval(async () => {
      try {
        // Include email as query parameter for database lookup if job not in memory
        const statusUrl = email
          ? `${API_URL}/Api/srt-generator/status/${jobId}?email=${encodeURIComponent(email)}`
          : `${API_URL}/Api/srt-generator/status/${jobId}`;
        console.log('Polling:', statusUrl);

        const response = await authFetch(statusUrl);

        if (!response.ok) {
          throw new Error(`Status check failed: ${response.status}`);
        }

        const data = await response.json();
        console.log('Job status:', data);

        // Reset error count on successful poll
        errorCount = 0;

        setProgress(data.progress || 0);
        setProgressMessage(data.message || 'Verwerken...');

        if (data.status === 'completed') {
          clearInterval(pollInterval);
          setGeneratedSRT(data.formattedSRT || data.originalSRT);
          setJobId(jobId); // Store job ID for later rendering
          setCorrectedSRT(data.correctedSRT);
          setFcpxml(data.fcpxml || null);
          setKaraokeASS(data.karaokeASS || null);
          setFrameioLink(data.frameio?.link || null);
          setHasBurnedVideo(data.hasBurnedVideo || false);
          setWordTimestamps(data.wordTimestamps || null);

          // Set AI corrections for individual review
          if (data.corrections && Array.isArray(data.corrections)) {
            setCorrections(data.corrections);
            // Default all corrections to 'pending' status
            const initialStatus = {};
            data.corrections.forEach((_, idx) => { initialStatus[idx] = 'pending'; });
            setCorrectionStatus(initialStatus);
          }

          // Check if Frame.io upload was requested but failed
          if (uploadToFrameIo && data.frameio && !data.frameio.success && data.frameio.error) {
            console.warn('Frame.io upload failed:', data.frameio.error);
            setError(`SRT gegenereerd, maar Frame.io upload mislukt: ${data.frameio.error}`);
          }

          // NOTE: Email is NOT sent here anymore - it's sent after rendering/approval in handleRender
          setFeedback(data.feedback);
          // Track detected language for UK/translation options
          if (data.detectedLanguage) {
            setDetectedLanguage(data.detectedLanguage);
          }
          // Set editable SRT for review step - use formatted version (with ellipsis + aspect ratio)
          setEditableSRT(data.formattedSRT || data.originalSRT || '');

          // Create video preview URL for audio playback in review
          if (videoFile) {
            const previewUrl = URL.createObjectURL(videoFile);
            setVideoPreviewUrl(previewUrl);
          }

          setLoading(false);
          setActiveStep(3); // Go to Review step
        } else if (data.status === 'error') {
          clearInterval(pollInterval);
          setError(data.error || 'Er ging iets mis bij de verwerking');
          setLoading(false);
        }
      } catch (err) {
        errorCount++;
        console.warn(`Poll error ${errorCount}/${maxErrors}:`, err.message);

        if (errorCount >= maxErrors) {
          clearInterval(pollInterval);
          setError('Kon status niet ophalen na meerdere pogingen. Probeer opnieuw.');
          setLoading(false);
        }
        // Otherwise, keep polling and hope it recovers
      }
    }, 2000);
  };

  const handleDownload = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Convert US English to UK English spelling (keeps timing identical)
  const convertToUKEnglish = async () => {
    const srtContent = correctedSRT || editableSRT;
    if (!srtContent) return;

    setConvertingToUK(true);
    try {

      const response = await authFetch(`${AI_ENGINE_URL}/api/v1/srt-jobs/convert-uk-english`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ srt_content: srtContent })
      });

      if (!response.ok) {
        throw new Error('Conversie mislukt');
      }

      const data = await response.json();
      setUkEnglishSRT(data.uk_srt);

      // Auto-download
      handleDownload(data.uk_srt, `${getBaseFilename()}_UK.srt`);

    } catch (err) {
      console.error('UK English conversion error:', err);
      setError(`UK English conversie mislukt: ${err.message}`);
    } finally {
      setConvertingToUK(false);
    }
  };

  // Load custom vocabulary from database
  const loadVocabulary = useCallback(async () => {
    try {
      const response = await authFetch(`${AI_ENGINE_URL}/api/v1/vocabulary/?active_only=true`);
      if (response.ok) {
        const data = await response.json();
        setVocabularyTerms(data);
      }
    } catch (err) {
      console.warn('Could not load vocabulary:', err);
    } finally {
      setVocabLoaded(true);
    }
  }, []);

  // Ctrl+Z / Cmd+Z buiten tekstvelden = laatste actie terugdraaien
  useEffect(() => registreerOngedaanSneltoets(), []);

  // Load vocabulary on mount
  useEffect(() => {
    if (!vocabLoaded) {
      loadVocabulary();
    }
  }, [vocabLoaded, loadVocabulary]);

  // Load client presets from AI Engine
  useEffect(() => {
    const loadPresets = async () => {
      try {
        const response = await authFetch(`${AI_ENGINE_URL}/api/v1/subtitle-presets/`);
        if (response.ok) {
          const allPresets = await response.json();
          setClientPresets(allPresets.filter(p => p.client_name !== '__default__'));
          // Find default preset for current aspect ratio
          const defPreset = allPresets.find(p => p.client_name === '__default__' && p.aspect_ratio === aspectRatio);
          setDefaultPreset(defPreset || null);
        }
      } catch (err) {
        console.warn('Could not load subtitle presets:', err);
      }
    };
    loadPresets();
  }, [aspectRatio]);

  // Load available fonts from backend
  const loadFonts = useCallback(async () => {
    try {
      const response = await authFetch(`${API_URL}/Api/srt-generator/fonts`);
      if (response.ok) {
        const data = await response.json();
        setAvailableFonts(data.fonts || []);
        setCustomFonts(data.customFonts || []);
      }
    } catch (err) {
      console.warn('Could not load fonts:', err);
    }
  }, []);

  useEffect(() => {
    loadFonts();
  }, [loadFonts]);

  // Upload a custom font file
  const handleFontUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // Reset input

    const formData = new FormData();
    formData.append('font', file);

    try {
      const response = await authFetch(`${API_URL}/Api/srt-generator/fonts/upload`, {
        method: 'POST',
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Font "${data.name}" geüpload`);
        await loadFonts();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.error || 'Font upload mislukt');
      }
    } catch (err) {
      toast.error('Font upload mislukt');
    }
  }, [loadFonts]);

  // Delete a custom font
  const handleFontDelete = useCallback(async (filename) => {
    if (!(await bevestig({ titel: 'Font verwijderen?', tekst: `"${filename}" wordt van de server verwijderd en is daarna voor niemand meer beschikbaar.`, knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const response = await authFetch(`${API_URL}/Api/srt-generator/fonts/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        toast.success('Font verwijderd');
        await loadFonts();
      }
    } catch (err) {
      toast.error('Font verwijderen mislukt');
    }
  }, [loadFonts]);

  // Update active preset when client or aspect ratio changes
  useEffect(() => {
    if (!selectedClientPreset || clientPresets.length === 0) {
      setActivePreset(null);
      return;
    }
    const match = clientPresets.find(
      p => p.client_name === selectedClientPreset && p.aspect_ratio === aspectRatio
    );
    setActivePreset(match || null);
    setOverridePreset(false);
  }, [selectedClientPreset, aspectRatio, clientPresets]);

  const addVocabularyTerm = async () => {
    if (!newVocabTerm.trim()) return;
    try {
      const response = await authFetch(`${AI_ENGINE_URL}/api/v1/vocabulary/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          term: newVocabTerm.trim(),
          replacement: newVocabReplacement.trim() || null,
          category: newVocabCategory.trim() || null,
          created_by: email || null
        })
      });
      if (response.ok) {
        const term = await response.json();
        setVocabularyTerms(prev => [term, ...prev]);
        setNewVocabTerm('');
        setNewVocabReplacement('');
        setNewVocabCategory('');
      }
    } catch (err) {
      console.error('Failed to add vocabulary term:', err);
    }
  };

  const deleteVocabularyTerm = async (termId) => {
    const verwijderd = vocabularyTerms.find(t => t.id === termId);
    try {
      const response = await authFetch(`${AI_ENGINE_URL}/api/v1/vocabulary/${termId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setVocabularyTerms(prev => prev.filter(t => t.id !== termId));
        if (verwijderd) {
          // Ongedaan maken = term opnieuw toevoegen (krijgt een nieuw id)
          biedOngedaanAan(`Term "${verwijderd.term}" verwijderd`, async () => {
            const r = await authFetch(`${AI_ENGINE_URL}/api/v1/vocabulary/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                term: verwijderd.term,
                replacement: verwijderd.replacement || null,
                category: verwijderd.category || null,
                created_by: email || null
              })
            });
            if (!r.ok) throw new Error('Term terugzetten mislukt');
            const term = await r.json();
            setVocabularyTerms(prev => [term, ...prev]);
          });
        }
      }
    } catch (err) {
      console.error('Failed to delete vocabulary term:', err);
    }
  };

  // Translate subtitles (NL <-> EN)
  const translateSubtitles = async () => {
    const srtContent = correctedSRT || editableSRT;
    if (!srtContent) return;

    const sourceLang = (detectedLanguage === 'nl' || detectedLanguage === 'dutch') ? 'nl' : 'en';
    const targetLang = sourceLang === 'nl' ? 'en' : 'nl';

    setTranslating(true);
    try {

      const response = await authFetch(`${AI_ENGINE_URL}/api/v1/srt-jobs/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          srt_content: srtContent,
          source_language: sourceLang,
          target_language: targetLang
        })
      });

      if (!response.ok) {
        throw new Error('Vertaling mislukt');
      }

      const data = await response.json();
      setTranslatedSRT(data.translated_srt);

      // Auto-download
      const suffix = targetLang === 'en' ? 'EN' : 'NL';
      handleDownload(data.translated_srt, `${getBaseFilename()}_${suffix}.srt`);

    } catch (err) {
      console.error('Translation error:', err);
      setError(`Vertaling mislukt: ${err.message}`);
    } finally {
      setTranslating(false);
    }
  };

  const handleReset = async () => {
    if (generatedSRT || editableSRT) {
      if (!(await bevestig({ titel: 'Nieuwe SRT beginnen?', tekst: 'De huidige ondertiteling en instellingen gaan verloren.', knop: 'Nieuwe SRT', gevaarlijk: true }))) return;
    }
    setActiveStep(0);
    setVideoFile(null);
    setFrameIoLink('');
    setScriptFile(null);
    setScriptText('');
    setEmail(activeAccount?.username || '');
    setGeneratedSRT(null);
    setCorrectedSRT(null);
    setFcpxml(null);
    setFrameioLink(null);
    setHasBurnedVideo(false);
    setFeedback('');
    setUkEnglishSRT(null);
    setTranslatedSRT(null);
    setDetectedLanguage(null);
    setSelectedFolderId(null);
    setSelectedFolderPath('');
    setError(null);
    setEmailSent(false);
    setSendEmail(true);
    setSubtitleStyle('box');
    setSubtitleSize('medium');
    setTimingOffset(0);
    setShowAdvanced(false);
    setVideoInfo(null);
    setLanguage('auto');
    setAspectRatio('16:9');
    setEditableSRT('');
    setJobId(null);
    setIsRendering(false);
    setCorrections([]);
    setCorrectionStatus({});
    setWordTimestamps(null);
    setShowWordEditor(false);
    setIsKaraoke(false);
    setKaraokeASS(null);
    emailAttemptedRef.current = false;
    // Clean up video preview URL to prevent memory leaks
    if (videoPreviewUrl) {
      URL.revokeObjectURL(videoPreviewUrl);
      setVideoPreviewUrl(null);
    }
  };

  // Track whether email was already attempted (prevent duplicate sends)
  const emailAttemptedRef = React.useRef(false);

  // Helper function to send email after completion
  const sendCompletionEmail = async (finalFrameioLink = null) => {
    if (!sendEmail || !email) return;
    if (emailAttemptedRef.current) return; // Already attempted, don't retry
    emailAttemptedRef.current = true;

    try {
      setProgressMessage('Email versturen...');

      // Build attachments array
      const attachments = [
        { filename: 'ondertiteling_origineel.srt', content: generatedSRT },
        { filename: 'ondertiteling_gecorrigeerd.srt', content: editableSRT || correctedSRT }
      ];

      // Add FCP-XML if available
      if (fcpxml) {
        attachments.push({ filename: 'ondertiteling.fcpxml', content: fcpxml });
      }

      await sendMailViaGraph(instance, activeAccount, {
        to: email,
        subject: '🎬 Je SRT ondertiteling is klaar!',
        body: buildSRTEmailHTML({
          feedback: feedback,
          frameioLink: finalFrameioLink,
          aspectRatio: aspectRatio,
          hasFcpxml: !!fcpxml
        }),
        attachments
      });
      setEmailSent(true);
      console.log('Email sent successfully to', email);
    } catch (emailError) {
      console.error('Email verzenden mislukt:', emailError);
      toast.error('Email verzenden mislukt');
      // Don't fail the whole process, just note it
      setError(`Voltooid, maar email versturen mislukt: ${emailError.message}`);
    }
  };

  // Save feedback to database for algorithm improvement
  const saveFeedbackToDatabase = async () => {
    try {

      // Determine which corrections were accepted/rejected
      const acceptedIndices = [];
      const rejectedIndices = [];
      Object.entries(correctionStatus).forEach(([idx, status]) => {
        if (status === 'accepted') acceptedIndices.push(parseInt(idx));
        if (status === 'rejected') rejectedIndices.push(parseInt(idx));
      });

      // Check if user made manual edits beyond accept/reject
      const hasManualEdits = editableSRT !== generatedSRT &&
        editableSRT !== (correctedSRT || generatedSRT);

      const feedbackData = {
        user_email: activeAccount?.username || 'unknown',
        filename: videoFile?.name || frameIoLink || 'unknown',
        language: language,
        aspect_ratio: aspectRatio,
        original_srt: generatedSRT || '',
        ai_corrected_srt: correctedSRT || null,
        final_srt: editableSRT || '',
        ai_corrections: corrections || [],
        corrections_accepted: acceptedIndices,
        corrections_rejected: rejectedIndices,
        ai_feedback: feedback || null,
        has_manual_edits: hasManualEdits,
        detected_language: language === 'auto' ? null : language
      };

      await authFetch(`${AI_ENGINE_URL}/api/v1/srt-jobs/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedbackData)
      });

      console.log('[SRT] Feedback saved for algorithm improvement');
    } catch (err) {
      // Don't fail the main flow if feedback saving fails
      console.warn('[SRT] Could not save feedback:', err.message);
    }
  };

  // Handle re-aligning timing after manual edits — calls /srt-generator/realign
  // which re-matches each cue's text to the original Whisper word-timings.
  const [isRealigning, setIsRealigning] = useState(false);
  const handleRealign = async () => {
    if (!editableSRT.trim()) {
      setSnackbar({ open: true, message: 'SRT is leeg.', severity: 'warning' });
      return;
    }
    if (!jobId) {
      setSnackbar({ open: true, message: 'Geen jobId — re-align niet mogelijk.', severity: 'warning' });
      return;
    }
    setIsRealigning(true);
    try {
      const res = await authFetch(`${API_URL}/Api/srt-generator/realign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jobId,
          editedSRT: editableSRT,
          aspectRatio,
          projectName: getBaseFilename(),
          presetStyle: (() => {
            const ep = (activePreset && !overridePreset) ? activePreset : defaultPreset;
            return ep ? { font_name: ep.font_name, font_style: ep.font_style, font_size: ep.font_size, baseline: ep.baseline } : null;
          })(),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setEditableSRT(data.realignedSRT || data.formattedSRT || editableSRT);
      if (data.formattedSRT) setCorrectedSRT(data.formattedSRT);
      if (data.fcpxml) setFcpxml(data.fcpxml);

      const msg = data.cuesFallback > 0
        ? `Timing bijgewerkt voor ${data.cuesRealigned} cues. ${data.cuesFallback} behielden originele timing.`
        : `Timing bijgewerkt voor ${data.cuesRealigned} cues.`;
      setSnackbar({ open: true, message: msg, severity: 'success' });
    } catch (err) {
      console.warn('[SRT] Realign failed:', err.message);
      setSnackbar({ open: true, message: `Re-align mislukt: ${err.message}`, severity: 'error' });
    } finally {
      setIsRealigning(false);
    }
  };

  // Handle rendering after review
  const handleRender = async () => {
    if (!editableSRT.trim()) {
      setError('SRT is leeg. Voeg ondertiteling toe.');
      return;
    }

    setIsRendering(true);
    setError(null);
    setProgress(0);
    setProgressMessage('Voorbereiden...');

    // Uitgesteld uitvoeren: 6 s "Annuleren" voordat er iets naar Frame.io/klant gaat.
    const voerRenderUit = async () => {
      // Save feedback in background (don't await to not slow down the flow)
      saveFeedbackToDatabase();

      try {
        // Format SRT (line-breaking) and generate matching FCP-XML
        // Both exports use the same formatted source → guaranteed in sync
        try {
          const formatResponse = await authFetch(`${API_URL}/Api/srt-generator/format`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              srtContent: editableSRT,
              aspectRatio,
              projectName: getBaseFilename(),
              presetStyle: (() => { const ep = (activePreset && !overridePreset) ? activePreset : defaultPreset; return ep ? { font_name: ep.font_name, font_style: ep.font_style, font_size: ep.font_size, baseline: ep.baseline } : null; })()
            })
          });
          if (formatResponse.ok) {
            const formatted = await formatResponse.json();
            setCorrectedSRT(formatted.formattedSRT);
            if (formatted.fcpxml) setFcpxml(formatted.fcpxml);
          } else {
            // Fallback: use raw SRT without formatting
            setCorrectedSRT(editableSRT);
          }
        } catch (formatErr) {
          console.warn('[SRT] Format endpoint failed, using raw SRT:', formatErr.message);
          setCorrectedSRT(editableSRT);
        }

        if (uploadToFrameIo) {
          setProgress(5);
          setProgressMessage('Render job starten...');

          // Call reprocess endpoint to start render and upload
          const response = await authFetch(`${API_URL}/Api/srt-generator/reprocess`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              jobId,
              srtContent: editableSRT,
              subtitleStyle,
              subtitleSize,
              subtitleFont: subtitleFont || activePreset?.font_name || null,
              aspectRatio,
              email,
              targetFolderId: selectedFolderId, // Upload to selected folder (null = same as source)
              isKaraoke,
              karaokeStyle: isKaraoke ? karaokeStyle : undefined
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Renderen starten mislukt');
          }

          const result = await response.json();

          // If the result already has a frameioLink, rendering is done
          if (result.frameioLink) {
            setFrameioLink(result.frameioLink);
            setProgress(100);
            setProgressMessage('Klaar!');
            // Send email with Frame.io link
            await sendCompletionEmail(result.frameioLink);
            setIsRendering(false);
            setActiveStep(4);
            return;
          }

          // If we got a jobId back, poll for progress
          const renderJobId = result.jobId || jobId;
          if (renderJobId) {
            // Email is sent inside pollRenderProgress when completed
            await pollRenderProgress(renderJobId);
          } else {
            // No job to poll, send email and complete
            await sendCompletionEmail(null);
            setIsRendering(false);
            setActiveStep(4);
          }
        } else {
          // No Frame.io upload, send email without Frame.io link
          await sendCompletionEmail(null);
          setIsRendering(false);
          setActiveStep(4);
        }

      } catch (err) {
        console.error('Render error:', err);
        // Still go to result step so user can download SRT files
        setError(`Render fout: ${err.message || 'Onbekende fout'}. Je kunt de SRT nog steeds downloaden.`);
        setIsRendering(false);
        setActiveStep(4); // Go to result step anyway - SRT files are still available
      }
    };

    if (!uploadToFrameIo && !sendEmail) {
      // Alleen lokaal renderen/downloaden: niets gaat naar buiten, dus geen wachttijd.
      await voerRenderUit();
      return;
    }
    setProgressMessage('Wacht op bevestiging… (annuleren kan nog)');
    const r = await voerUitgesteldUit('Ondertitels gaan naar Frame.io en de klant krijgt een mail…', voerRenderUit, { duur: 6000 });
    if (r.geannuleerd) {
      setIsRendering(false);
      setProgress(0);
      setProgressMessage('');
    }
  };

  // Poll for render progress
  const pollRenderProgress = async (renderJobId) => {
    return new Promise((resolve, reject) => {
      let errorCount = 0;
      const maxErrors = 10;
      let pollCount = 0;
      const startTime = Date.now();

      // Wait 1 second before first poll to ensure backend has updated the job
      setTimeout(() => {
        const pollInterval = setInterval(async () => {
          pollCount++;
          try {
            const statusUrl = email
              ? `${API_URL}/Api/srt-generator/status/${renderJobId}?email=${encodeURIComponent(email)}`
              : `${API_URL}/Api/srt-generator/status/${renderJobId}`;

            const response = await authFetch(statusUrl);

            if (!response.ok) {
              throw new Error(`Status check failed: ${response.status}`);
            }

            const data = await response.json();
            console.log(`Render status poll #${pollCount}:`, data);

            errorCount = 0; // Reset on success

            // Update progress
            setProgress(data.progress || 0);
            setProgressMessage(data.message || 'Renderen...');

            // Ignore 'completed' status in first 5 seconds - it's likely stale data from previous job
            const elapsedSeconds = (Date.now() - startTime) / 1000;
            const ignoreCompletedUntil = 5; // seconds

            if (data.status === 'completed') {
              // Only accept completion if we have a new frameio link OR enough time has passed
              const hasNewFrameioLink = data.frameio?.uploadLink;

              if (hasNewFrameioLink || elapsedSeconds > ignoreCompletedUntil) {
                clearInterval(pollInterval);
                const finalLink = hasNewFrameioLink ? data.frameio.uploadLink : null;
                if (finalLink) {
                  setFrameioLink(finalLink);
                }
                setProgress(100);
                setProgressMessage('Email versturen...');

                // Send completion email with Frame.io link
                await sendCompletionEmail(finalLink);

                setProgressMessage('Klaar!');
                setIsRendering(false);
                setActiveStep(4);
                resolve(finalLink);
              } else {
                console.log(`Ignoring stale 'completed' status (${elapsedSeconds.toFixed(1)}s elapsed, need ${ignoreCompletedUntil}s)`);
              }
            } else if (data.status === 'error') {
              clearInterval(pollInterval);
              console.error('Render error:', data.error);
              // Still go to result step so user can download SRT files
              setError(`Frame.io render mislukt: ${data.error || 'Onbekende fout'}. Je kunt de SRT nog steeds downloaden.`);
              setIsRendering(false);
              setActiveStep(4); // Go to result step anyway
              resolve(null); // Resolve with null (no frameio link)
            }
          } catch (err) {
            errorCount++;
            console.warn(`Render poll error ${errorCount}/${maxErrors}:`, err.message);

            if (errorCount >= maxErrors) {
              clearInterval(pollInterval);
              // Still go to result step so user can download SRT files
              setError('Frame.io upload timeout. Je kunt de SRT nog steeds downloaden.');
              setIsRendering(false);
              setActiveStep(4); // Go to result step anyway
              resolve(null);
            }
          }
        }, 2000);
      }, 1000); // Wait 1 second before starting to poll
    });
  };

  // Get filename for downloads (same as video name)
  const getBaseFilename = () => {
    const videoName = videoFile?.name || frameIoLink?.split('/').pop() || 'ondertiteling';
    return videoName.replace(/\.[^.]+$/, '');
  };

  // Computed font options for FontPicker
  const allFontOptions = [
    ...customFonts.map((cf) => ({ label: cf.name, value: cf.name, group: 'Geüploade fonts', filename: cf.filename })),
    ...availableFonts.map((f) => ({ label: f, value: f, group: 'Systeem fonts' })),
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6, minHeight: 'calc(100vh - 100px)' }}>
      <PageHeader
        icon="subtitles"
        title="SRT Generator"
        subtitle="Genereer automatisch ondertitels uit video of audio met behulp van AI"
        accent={{ color: '#635983', bg: '#dbcdff' }}
      />
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => setError(null)}
            action={
              error.includes('Video niet meer beschikbaar') || error.includes('Genereer opnieuw') ? (
                <Button color="inherit" size="small" onClick={handleReset}>
                  Start Opnieuw
                </Button>
              ) : null
            }
          >
            {error}
          </Alert>
        )}

        {activeStep === 0 && (
          <StepVideoInput
            inputMethod={inputMethod}
            setInputMethod={setInputMethod}
            convertingWav={convertingWav}
            wavConversionProgress={wavConversionProgress}
            isVideoDragActive={isVideoDragActive}
            getVideoRootProps={getVideoRootProps}
            getVideoInputProps={getVideoInputProps}
            videoFile={videoFile}
            setVideoFile={setVideoFile}
            videoInfo={videoInfo}
            setVideoInfo={setVideoInfo}
            analyzing={analyzing}
            frameIoLink={frameIoLink}
            setFrameIoLink={setFrameIoLink}
            analyzeVideo={analyzeVideo}
          />
        )}

        {activeStep === 1 && (
          <StepSettings
            language={language} setLanguage={setLanguage}
            aspectRatio={aspectRatio} setAspectRatio={setAspectRatio}
            videoInfo={videoInfo}
            clientPresets={clientPresets}
            selectedClientPreset={selectedClientPreset} setSelectedClientPreset={setSelectedClientPreset}
            activePreset={activePreset}
            defaultPreset={defaultPreset}
            setPresetManagerOpen={setPresetManagerOpen}
            getScriptRootProps={getScriptRootProps}
            getScriptInputProps={getScriptInputProps}
            scriptFile={scriptFile} setScriptFile={setScriptFile}
            scriptText={scriptText} setScriptText={setScriptText}
            skipCorrection={skipCorrection} setSkipCorrection={setSkipCorrection}
            overridePreset={overridePreset} setOverridePreset={setOverridePreset}
            subtitleStyle={subtitleStyle} setSubtitleStyle={setSubtitleStyle}
            subtitleSize={subtitleSize} setSubtitleSize={setSubtitleSize}
            subtitleFont={subtitleFont} setSubtitleFont={setSubtitleFont}
            ellipsisStyle={ellipsisStyle} setEllipsisStyle={setEllipsisStyle}
            allFontOptions={allFontOptions}
            handleFontDelete={handleFontDelete}
            fontInputRef={fontInputRef}
            handleFontUpload={handleFontUpload}
            isKaraoke={isKaraoke} setIsKaraoke={setIsKaraoke}
            karaokeStyle={karaokeStyle} setKaraokeStyle={setKaraokeStyle}
            setKaraokePresetManagerOpen={setKaraokePresetManagerOpen}
            vocabularyTerms={vocabularyTerms}
            newVocabTerm={newVocabTerm} setNewVocabTerm={setNewVocabTerm}
            newVocabReplacement={newVocabReplacement} setNewVocabReplacement={setNewVocabReplacement}
            newVocabCategory={newVocabCategory} setNewVocabCategory={setNewVocabCategory}
            addVocabularyTerm={addVocabularyTerm}
            deleteVocabularyTerm={deleteVocabularyTerm}
            sendEmail={sendEmail} setSendEmail={setSendEmail}
            uploadToFrameIo={uploadToFrameIo} setUploadToFrameIo={setUploadToFrameIo}
            email={email} setEmail={setEmail}
            selectedFolderId={selectedFolderId} setSelectedFolderId={setSelectedFolderId}
            selectedFolderPath={selectedFolderPath} setSelectedFolderPath={setSelectedFolderPath}
            frameIoLink={frameIoLink}
            activeAccount={activeAccount}
          />
        )}

        {activeStep === 2 && (
          <StepGenerate
            videoFile={videoFile}
            frameIoLink={frameIoLink}
            language={language}
            aspectRatio={aspectRatio}
            scriptText={scriptText}
            sendEmail={sendEmail}
            email={email}
            uploadToFrameIo={uploadToFrameIo}
            selectedFolderPath={selectedFolderPath}
            loading={loading}
            progressMessage={progressMessage}
            progress={progress}
          />
        )}

        {activeStep === 3 && (
          <StepReview
            feedback={feedback}
            corrections={corrections}
            correctionStatus={correctionStatus} setCorrectionStatus={setCorrectionStatus}
            correctionEdits={correctionEdits} setCorrectionEdits={setCorrectionEdits}
            editableSRT={editableSRT} setEditableSRT={setEditableSRT}
            generatedSRT={generatedSRT}
            jobId={jobId}
            handleRealign={handleRealign}
            isRealigning={isRealigning}
            videoPreviewUrl={videoPreviewUrl}
            videoRef={videoRef}
            playbackTime={playbackTime} setPlaybackTime={setPlaybackTime}
            wordTimestamps={wordTimestamps}
            showWordEditor={showWordEditor} setShowWordEditor={setShowWordEditor}
            aspectRatio={aspectRatio}
            getBaseFilename={getBaseFilename}
            uploadToFrameIo={uploadToFrameIo}
            isRendering={isRendering}
            handleBack={handleBack}
            handleRender={handleRender}
            progress={progress}
            progressMessage={progressMessage}
          />
        )}

        {activeStep === 4 && (
          <StepResult
            emailSent={emailSent}
            email={email}
            correctedSRT={correctedSRT}
            editableSRT={editableSRT}
            fcpxml={fcpxml}
            karaokeASS={karaokeASS}
            generatedSRT={generatedSRT}
            detectedLanguage={detectedLanguage}
            ukEnglishSRT={ukEnglishSRT}
            convertingToUK={convertingToUK}
            convertToUKEnglish={convertToUKEnglish}
            translatedSRT={translatedSRT}
            translating={translating}
            translateSubtitles={translateSubtitles}
            uploadToFrameIo={uploadToFrameIo}
            frameioLink={frameioLink}
            hasBurnedVideo={hasBurnedVideo}
            jobId={jobId}
            handleReset={handleReset}
            emailAttemptedRef={emailAttemptedRef}
            setActiveStep={setActiveStep}
            setIsRendering={setIsRendering}
            aspectRatio={aspectRatio}
            activePreset={activePreset}
            overridePreset={overridePreset}
            defaultPreset={defaultPreset}
            getBaseFilename={getBaseFilename}
          />
        )}

        {/* Navigation buttons for steps 0-2 (step 3 has its own buttons, step 4 has its own buttons) */}
        {activeStep < 3 && !loading && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Vorige
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={analyzing}
            >
              {activeStep === 2 ? 'Genereer SRT' : 'Volgende'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Preset Manager Dialog */}
      <PresetManager
        open={presetManagerOpen}
        onClose={() => setPresetManagerOpen(false)}
        availableFonts={availableFonts}
        customFonts={customFonts}
        onFontUpload={handleFontUpload}
        fontInputRef={fontInputRef}
        onPresetsChanged={async () => {
          try {
            const AI_ENGINE = AI_ENGINE_URL;
            const res = await authFetch(`${AI_ENGINE}/api/v1/subtitle-presets/`);
            if (res.ok) {
              const allPresets = await res.json();
              setClientPresets(allPresets.filter(p => p.client_name !== '__default__'));
              const defPreset = allPresets.find(p => p.client_name === '__default__' && p.aspect_ratio === aspectRatio);
              setDefaultPreset(defPreset || null);
            }
          } catch {}
        }}
      />

      {/* Social Subs Preset Manager Dialog */}
      <KaraokePresetManager
        open={karaokePresetManagerOpen}
        onClose={() => setKaraokePresetManagerOpen(false)}
        karaokeStyle={karaokeStyle}
        onLoadPreset={(style) => setKaraokeStyle((s) => ({ ...s, ...style }))}
      />
    </Box>
  );
}

export default SRTGenerator;
